import { useQuery } from '@tanstack/react-query'
import { differenceInCalendarDays, parseISO } from 'date-fns'

import { Avatar, Cartao, Esqueleto, Vazio } from '@/componentes/ui'
import { formatarData } from '@/lib/datas'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utilitarios'

interface LinhaEngajamento {
  matricula_id: string
  user_id: string
  nome: string | null
  email: string | null
  status: string
  onboarding_completo: boolean
  data_inicio: string
  turma_id: string
  turma_nome: string
  duracao_dias: number
  dia_atual: number
  total_diarios: number
  ultimo_diario: string | null
  total_missoes_concluidas: number
}

type Alerta = 'sumida' | 'atencao' | 'em_dia' | 'onboarding' | 'concluida'

/**
 * Acompanhamento de adesão (não faz parte da seção 14 da especificação,
 * mas resolve o problema que o app inteiro existe para resolver: no
 * PDF anterior, ninguém sabia quem estava seguindo o programa até ser
 * tarde demais para ajudar).
 *
 * Lê `vw_engajamento_participantes`, uma view que agrega diários e
 * missões concluídas por matrícula — os dados já existiam espalhados
 * em três tabelas, isto só os junta numa consulta.
 */
export function SecaoAcompanhamento() {
  const { data: linhas = [], isLoading } = useQuery({
    queryKey: ['admin', 'engajamento'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_engajamento_participantes')
        .select('*')
        .in('status', ['ativa', 'concluida'])

      if (error) throw error
      return data as LinhaEngajamento[]
    },
    staleTime: 60 * 1000,
  })

  const ativas = linhas.filter((l) => l.status === 'ativa')
  const concluidas = linhas.filter((l) => l.status === 'concluida')

  const ordenadas = [...ativas].sort((a, b) => diasSemDiario(b) - diasSemDiario(a))
  const sumidas = ordenadas.filter((l) => alertaDe(l) === 'sumida').length

  if (isLoading) return <Esqueleto className="h-96" />

  return (
    <div className="flex flex-col gap-4">
      <Cartao className="bg-dourado/15 text-sm leading-relaxed text-[var(--tinta-suave)]">
        Quem preenche o diário todo dia aparece em verde. Quem some por alguns dias vira amarelo, e
        depois vermelho — é aí que vale mandar uma mensagem antes que ela desista de vez.
      </Cartao>

      {sumidas > 0 && (
        <p className="font-titulo text-sm font-semibold text-pink">
          {sumidas} {sumidas === 1 ? 'participante sumida' : 'participantes sumidas'} há 4+ dias
        </p>
      )}

      {ordenadas.length === 0 ? (
        <Vazio emoji="📊" titulo="Ninguém em turma ativa no momento" />
      ) : (
        <div className="flex flex-col gap-2">
          {ordenadas.map((linha) => (
            <LinhaParticipante key={linha.matricula_id} linha={linha} />
          ))}
        </div>
      )}

      {/* Sem os 28 dias já passados, lembretes desligados — a
          participante só some das outras listas do painel. Mantê-las
          visíveis aqui, ainda que como histórico, é o que evita a
          Raquel perder de vista quem já terminou. */}
      {concluidas.length > 0 && (
        <>
          <p className="mt-2 font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
            🎓 Concluíram o programa ({concluidas.length})
          </p>
          <div className="flex flex-col gap-2">
            {concluidas.map((linha) => (
              <LinhaParticipante key={linha.matricula_id} linha={linha} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function diasSemDiario(linha: LinhaEngajamento) {
  if (!linha.ultimo_diario) return linha.dia_atual + 1000 // nunca preencheu: pior caso, sempre no topo
  return differenceInCalendarDays(new Date(), parseISO(linha.ultimo_diario))
}

function alertaDe(linha: LinhaEngajamento): Alerta {
  if (linha.status === 'concluida') return 'concluida'
  if (!linha.onboarding_completo) return 'onboarding'
  const dias = diasSemDiario(linha)
  if (dias >= 4) return 'sumida'
  if (dias >= 2) return 'atencao'
  return 'em_dia'
}

const ESTILO_ALERTA: Record<Alerta, { cor: string; texto: string; borda: string }> = {
  sumida: { cor: 'bg-pink/15 text-pink', texto: 'Sumida', borda: 'border-l-pink' },
  atencao: { cor: 'bg-dourado/25 text-marrom', texto: 'Atenção', borda: 'border-l-dourado' },
  em_dia: { cor: 'bg-verde/15 text-verde', texto: 'Em dia', borda: 'border-l-verde' },
  onboarding: {
    cor: 'bg-[var(--borda)] text-[var(--tinta-apagada)]',
    texto: 'No cadastro',
    borda: 'border-l-[var(--borda)]',
  },
  concluida: {
    cor: 'bg-coral/15 text-coral',
    texto: 'Concluída',
    borda: 'border-l-coral',
  },
}

function LinhaParticipante({ linha }: { linha: LinhaEngajamento }) {
  const alerta = alertaDe(linha)
  const estilo = ESTILO_ALERTA[alerta]
  const adesao = Math.min(100, Math.round((linha.total_diarios / linha.dia_atual) * 100))

  return (
    <Cartao className={cn('border-l-4 p-4', estilo.borda)}>
      <div className="flex items-center gap-3">
        <Avatar nome={linha.nome} email={linha.email} />

        <div className="min-w-0 flex-1">
          <p className="truncate font-titulo text-[15px] font-semibold">
            {linha.nome || linha.email || 'Sem nome'}
          </p>
          <p className="truncate text-xs text-[var(--tinta-apagada)]">
            {linha.turma_nome} · Dia {Math.max(1, linha.dia_atual)} de {linha.duracao_dias}
          </p>
        </div>

        <span className={cn('shrink-0 rounded-full px-3 py-1 text-xs font-medium', estilo.cor)}>
          {estilo.texto}
        </span>
      </div>

      {linha.onboarding_completo ? (
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="font-titulo text-lg font-bold">{adesao}%</p>
            <p className="text-[11px] text-[var(--tinta-apagada)]">adesão ao diário</p>
          </div>
          <div>
            <p className="font-titulo text-lg font-bold">{linha.total_diarios}</p>
            <p className="text-[11px] text-[var(--tinta-apagada)]">diários preenchidos</p>
          </div>
          <div>
            <p className="font-titulo text-lg font-bold">
              {linha.ultimo_diario ? formatarData(linha.ultimo_diario, 'd/MM') : '—'}
            </p>
            <p className="text-[11px] text-[var(--tinta-apagada)]">último diário</p>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-xs text-[var(--tinta-apagada)]">
          Ainda não terminou o cadastro inicial.
        </p>
      )}
    </Cartao>
  )
}
